import styled from 'styled-components';

import { Text } from '../../cdl/Text/Text';
import { translate } from '../helpers/translate.helper';
import { useRole } from '../hooks/useRole';
import { useSidebar } from '../hooks/useSidebar';
import { CloselinkDiamond } from '../icons/closelink/CloselinkDiamond';
import { Box } from '../ui/Box';
import { theme } from '../ui/theme';

import { AdminSidebarNavigation } from './admin/AdminSidebarNavigation';
import { SidebarEdgeToggle } from './components/SidebarEdgeToggle';
import { SidebarMenu } from './components/SidebarMenu';
import { SidebarProFeaturesBanner } from './components/SidebarProFeaturesBanner';
import { CustomerSidebarNavigation } from './customer/CustomerSidebarNavigation';
import { SupplierSidebarNavigation } from './supplier/SupplierSidebarNavigation';

interface WrapperProps {
    $isFixed: boolean;
    $isHovered: boolean;
}

const SidebarContainer = styled.div<WrapperProps>`
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 250px; /* 220px sidebar + 30px button */
    height: 100%;
    transform: translateX(${({ $isFixed, $isHovered }) => ($isFixed || $isHovered ? '0' : '-220px')});
    transition: transform 0.15s ease-out;
    will-change: transform;
    z-index: ${theme.zIndex.sidebar};
    pointer-events: none; /* Let children handle their own pointer events */
`;

const SidebarWrapper = styled.div<WrapperProps>`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 220px;
    background: linear-gradient(90deg, #fff 0%, #f9f9fa 100%);
    display: grid;
    grid-template-rows: 60px auto;
    height: 100%;
    padding: 0 ${theme.space[16]};
    pointer-events: ${({ $isFixed, $isHovered }) => ($isFixed || $isHovered ? 'auto' : 'none')};
    box-shadow: ${({ $isFixed, $isHovered }) => (!$isFixed && $isHovered ? theme.shadows.extraLarge : 'none')};
`;

const ClickableArea = styled.div<WrapperProps>`
    flex: 1;
    cursor: ${({ $isFixed, $isHovered }) => (!$isFixed && $isHovered ? 'pointer' : 'default')};
    pointer-events: ${({ $isFixed, $isHovered }) => (!$isFixed && $isHovered ? 'auto' : 'none')};
`;

const SidebarProductHeader = () => (
    <Box display="grid" gridTemplateColumns="24px auto" alignItems="center" gap={8} marginLeft={8} marginTop={8}>
        <CloselinkDiamond color="blue" width={24} />
        <Text variant="subtitle1">{translate('common.lubricants')}</Text>
    </Box>
);

export const Sidebar = () => {
    const role = useRole();
    const { isFixed, isHovered, setHovered, show } = useSidebar();

    const handleMouseEnter = () => {
        if (!isFixed) {
            setHovered(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isFixed) {
            setHovered(false);
        }
    };

    const handleClickableAreaClick = () => {
        if (!isFixed && isHovered) {
            show();
        }
    };

    return (
        <SidebarContainer $isFixed={isFixed} $isHovered={isHovered}>
            <SidebarWrapper
                $isFixed={isFixed}
                $isHovered={isHovered}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Box paddingBottom={0} paddingTop={8}>
                    <SidebarProductHeader />
                </Box>

                <Box display="flex" flexDirection="column" paddingTop={8} paddingBottom={16} height="100%" gap={16}>
                    <div>
                        {role.isSuperAdmin() ? <AdminSidebarNavigation /> : null}
                        {role.isCustomer() ? <CustomerSidebarNavigation /> : null}
                        {role.isSupplier() ? <SupplierSidebarNavigation /> : null}
                    </div>

                    <ClickableArea $isFixed={isFixed} $isHovered={isHovered} onClick={handleClickableAreaClick} />

                    {!role.isSuperAdmin() ? <SidebarProFeaturesBanner /> : null}

                    <SidebarMenu />
                </Box>
            </SidebarWrapper>
            <SidebarEdgeToggle />
        </SidebarContainer>
    );
};
